import { motion } from "framer-motion";
import {
  LifeBuoy,
  Route,
  BrainCircuit,
  Rocket,
  Zap,
  HeartPulse,
  Cpu,
  Landmark,
  Clapperboard,
  Megaphone,
  Target,
  Bot,
  Plus,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const IMG_SKYSCRAPER =
  "https://images.unsplash.com/photo-1603294278610-b5bd0506303e?crop=entropy&cs=srgb&fm=jpg&q=85";

const CARDS = [
  {
    id: "service-card-project-delivery",
    icon: Route,
    badge: "Short & long-term PM",
    title: "End-to-End Project Delivery",
    desc: "Full lifecycle PM leadership. From zero-state scoping to executive signoff, we supply vetted senior PMs and technical delivery leads matched to your industry.",
    features: [
      "Agile & waterfall governance",
      "Sprint acceleration & milestone control",
      "Risk management that actually gets used",
    ],
    span: "md:col-span-4",
  },
  {
    id: "service-card-ai-readiness",
    icon: BrainCircuit,
    badge: "Strategic audit",
    title: "AI Readiness Assessment",
    desc: "Evaluate your infrastructure, data pipelines, regulatory guardrails and workforce capability before committing millions to AI tooling.",
    features: [
      "Data & infrastructure readiness scorecard",
      "Risk & compliance guardrails",
      "ROI feasibility modelling",
    ],
    span: "md:col-span-4",
  },
  {
    id: "service-card-ai-implementation",
    icon: Rocket,
    badge: "Deployment & adoption",
    title: "AI Implementation Programs",
    desc: "Executing complex multi-vendor AI programmes with verifiable timelines and zero operational drift.",
    features: [
      "Multi-vendor AI programme delivery",
      "MLOps & agent workflow governance",
      "Adoption, training & change management",
    ],
    span: "md:col-span-6",
  },
  {
    id: "service-card-quick-turnaround",
    icon: Zap,
    badge: "Sprint PM",
    title: "Quick-Turnaround Projects & Rapid Audits",
    desc: "Must launch in weeks? Need an independent sanity audit now? We supply battle-tested sprint leads for projects that cannot wait.",
    features: [
      "Rapid launch & sprint leadership",
      "Independent project audits",
      "Bottleneck liquidation",
    ],
    span: "md:col-span-6",
  },
];

const SECTORS = [
  { label: "Healthcare", icon: HeartPulse },
  { label: "Tech", icon: Cpu },
  { label: "Finance", icon: Landmark },
  { label: "Media", icon: Clapperboard },
  { label: "Marketing", icon: Megaphone },
  { label: "Sales Plan", icon: Target },
  { label: "AI Project Implementation", icon: Bot },
];

function Feature({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-slate-400">
      <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" strokeWidth={3} />
      {children}
    </li>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="scroll-mt-24 py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mb-14 flex flex-col gap-5 lg:mb-16 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                Capabilities
              </p>
              <h2 className="max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                Full-lifecycle delivery. Instant-response rescue.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              However far along — or far gone — your project is, we plug in
              senior command and restore momentum.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-12 lg:gap-6">
          <Reveal className="md:col-span-8 md:row-span-2" delay={0.05}>
            <motion.article
              data-testid="service-card-project-rescue"
              whileHover={{ y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-[#13192B] via-[#0E1424] to-[#0A0E18] p-7 lg:p-10"
            >
              <div className="absolute inset-y-0 right-0 hidden w-2/5 md:block">
                <img
                  src={IMG_SKYSCRAPER}
                  alt="Enterprise infrastructure"
                  className="h-full w-full object-cover opacity-35 duotone-gold transition-all duration-700 group-hover:opacity-50"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0E1424] via-[#0E1424]/70 to-transparent" />
                <div className="absolute inset-0 scanlines opacity-60" />
                <span className="absolute right-4 top-4 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">
                  51.5074° N / Tier-1 infra
                </span>
              </div>

              <div className="relative max-w-lg">
                <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  <LifeBuoy className="h-3.5 w-3.5" />
                  Flagship — Rapid response
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Project Rescue & Crisis Assurance
                </h3>
                <p className="mt-4 leading-relaxed text-slate-400">
                  Immediate intervention for projects facing critical timeline
                  slippage, vendor disputes, architecture bottlenecks or runaway
                  budgets. We triage fast and insert a seasoned Project
                  Commander to turn chaos into deterministic delivery.
                </p>
                <ul className="mt-7 space-y-3">
                  <Feature>Forensic project triage & recovery plan</Feature>
                  <Feature>Stakeholder & vendor realignment</Feature>
                  <Feature>Scope, budget & timeline recovery</Feature>
                  <Feature>Board-ready assurance reporting</Feature>
                </ul>
              </div>
            </motion.article>
          </Reveal>

          {CARDS.slice(0, 2).map((c, i) => (
            <Reveal key={c.id} className={c.span} delay={0.1 + i * 0.06}>
              <motion.article
                data-testid={c.id}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-[#0E1424] p-7 transition-colors duration-300 hover:border-white/25 lg:p-8"
              >
                <div className="flex items-center justify-between gap-3">
                  <c.icon className="h-6 w-6 text-gold" strokeWidth={1.75} />
                  <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
                    {c.badge}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-white sm:text-xl">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
              </motion.article>
            </Reveal>
          ))}

          {CARDS.slice(2).map((c, i) => (
            <Reveal key={c.id} className={c.span} delay={0.1 + i * 0.06}>
              <motion.article
                data-testid={c.id}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
                className="group flex h-full flex-col rounded-2xl border border-white/10 bg-[#0E1424] p-7 transition-colors duration-300 hover:border-white/25 lg:p-8"
              >
                <div className="flex items-center justify-between gap-3">
                  <c.icon className="h-6 w-6 text-gold" strokeWidth={1.75} />
                  <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500">
                    {c.badge}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-white sm:text-xl">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
              </motion.article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div id="industries" className="mt-16 scroll-mt-28 border-t border-white/8 pt-12">
            <div className="mb-8 flex items-center gap-4">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                Sectors we operate in
              </p>
              <span className="h-px flex-1 bg-white/8" />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
              {SECTORS.map((s) => (
                <div
                  key={s.label}
                  className="group flex flex-col gap-3 rounded-xl border border-white/8 bg-abyss/60 p-4 transition-all duration-300 hover:border-gold/40 hover:bg-plate"
                >
                  <s.icon className="h-5 w-5 text-slate-500 transition-colors duration-300 group-hover:text-gold" />
                  <span className="text-xs font-medium leading-snug text-slate-300">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
